import React, { useEffect, useRef, useState } from 'react';
import { Award, BookOpen, Users, Target, CheckCircle, ChevronLeft, ChevronRight, PlayCircle, X } from 'lucide-react';

const About = () => {
  const doctors = [
    {
      name: "Dr. Deep Maheshwari",
      title: "Co-Founder & Sports Physiotherapist",
      image: "/images/deep.jpeg", // Placeholder: User to place actual image here
      specialties: ["Sports Physiotherapy, Professional Sports, Clinical Rehab"],
      experience: "7+ Years",
      education: "BPTh/BPT (Devi Ahilya Vishwa Vidhyalaya, 2018), MPTh/MPT (Sports Physiotherapy, Jamia Hamdard, 2021)",
      quote: "AIFF-registered sports physiotherapist. Former I-League club physiotherapist, official physiotherapist for AITA tennis events, team & residential physiotherapist for Sudeva Delhi Football Club (Oct 2021–Dec 2022), on-field physiotherapist at Sajid Lodi Tennis Academy, ITC Indore (Oct 2018–Jul 2019). Registration: SCH-01/DEG2/53420/2023."
    },
    {
      name: "Dr. Pragati Nayak",
      title: "Co-Founder & Lead Physiotherapist",
      image: "/images/pragati.jpg", // Placeholder: User to place actual image here
      specialties: ["Orthopedic, Cardio, Neuro, ICU, COVID-19 Rehab"],
      experience: "6+ Years",
      education: "BPT (Devi Ahilya Vishwa Vidhyalaya, 2018), MPT (CardioThoracic, Devi Ahilya Vishwa Vidhyalaya, 2020)",
      quote: "Co-founder of Arpan PhysioCure™ (2018). Registered Practitioner with NCAHP and MP Paramedical Council. Managed acute and chronic cases across orthopedic, cardiopulmonary, neurological, and ICU settings. COVID-19 duty at SAIMS and MY Hospital, on-field physiotherapy for local runs, and IPD work in cardiac and respiratory ICUs. Key roles: COVID-19 physiotherapy in SAIMS (Jun 2020), ICU physiotherapy at MY Hospital and Sri Aurobindo Hospital, On-field physiotherapist for 13K Plog Run and Bhopal Run (Dec 2018), Volunteer in government-organised COVID surveys. Certifications: AIFF, NCAHP, MPPMC, AITA, FIFA."
    }
  ];

  const achievements = [
    { icon: Users, title: "100+", subtitle: "Patients Treated Successfully" },
    { icon: Award, title: "AIFF & AITA", subtitle: "Certified Sports Physiotherapists" },
    { icon: BookOpen, title: "15+", subtitle: "Years Combined Experience" },
    { icon: Target, title: "98%", subtitle: "Patient Satisfaction Rate" }
  ];

  const values = [
    {
      title: "Patient-Centered Care",
      description: "Every treatment plan is tailored to individual needs and goals"
    },
    {
      title: "Evidence-Based Practice",
      description: "We use the latest research and proven techniques for optimal outcomes"
    },
    {
      title: "Continuous Learning",
      description: "Regular training and certification in advanced physiotherapy methods"
    },
    {
      title: "Holistic Approach",
      description: "Treating the whole person, not just the condition"
    }
  ];

  // Facilities gallery and media
  const facilityGallery = [
    { title: '6-bed Private Facility', image: 'https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg?auto=compress&cs=tinysrgb&w=1200' },
    { title: 'Electrotherapy & Ultrasound', image: 'https://images.pexels.com/photos/4506106/pexels-photo-4506106.jpeg?auto=compress&cs=tinysrgb&w=1200' },
    { title: 'Traction & Decompression', image: 'https://images.pexels.com/photos/4491459/pexels-photo-4491459.jpeg?auto=compress&cs=tinysrgb&w=1200' },
    { title: 'Pneumatic Compression', image: 'https://images.pexels.com/photos/4506109/pexels-photo-4506109.jpeg?auto=compress&cs=tinysrgb&w=1200' },
    { title: 'Exercise & Rehab Gym', image: 'https://images.pexels.com/photos/4386466/pexels-photo-4386466.jpeg?auto=compress&cs=tinysrgb&w=1200' },
    { title: 'Balance & Mobility Trainers', image: 'https://images.pexels.com/photos/7551544/pexels-photo-7551544.jpeg?auto=compress&cs=tinysrgb&w=1200' },
    { title: 'Pediatric Rehab Aids', image: 'https://images.pexels.com/photos/8613061/pexels-photo-8613061.jpeg?auto=compress&cs=tinysrgb&w=1200' },
    { title: 'Longwave, LASER & IFT', image: 'https://images.pexels.com/photos/7551667/pexels-photo-7551667.jpeg?auto=compress&cs=tinysrgb&w=1200' }
  ];
  const facilityVideoUrl = 'https://www.instagram.com/p/C7tYK76sQvE';
  const [showVideoModal, setShowVideoModal] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const scrollByCards = (dir: 'left' | 'right') => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.8);
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  // Close modal on ESC
  useEffect(() => {
    if (!showVideoModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowVideoModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showVideoModal]);

  // Load Instagram embed script when modal opens
  useEffect(() => {
    if (!showVideoModal) return;
    const existing = document.getElementById('instagram-embed-js') as HTMLScriptElement | null;
    if (!existing) {
      const s = document.createElement('script');
      s.id = 'instagram-embed-js';
      s.async = true;
      s.src = 'https://www.instagram.com/embed.js';
      s.onload = () => {
        // @ts-ignore
        window.instgrm && window.instgrm.Embeds && window.instgrm.Embeds.process();
      };
      document.body.appendChild(s);
    } else {
      // @ts-ignore
      window.instgrm && window.instgrm.Embeds && window.instgrm.Embeds.process();
    }
  }, [showVideoModal]);

  return (
    <section id="about" className="relative pt-12 sm:pt-14 pb-16 sm:pb-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden scroll-mt-28 sm:scroll-mt-32 lg:scroll-mt-40">
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-40"></div>
      <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 bg-purple-100 rounded-full blur-3xl opacity-40"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-5">
            Meet Our Expert Team
          </h2>
          <div className="mx-auto mt-2 h-1.5 w-20 bg-gradient-to-r from-blue-600 to-indigo-500 rounded-full"></div>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto px-4 mt-4">
            Led by experienced physiotherapists with specialized training and a passion 
            for helping patients achieve their health and wellness goals.
          </p>
        </div>

        {/* Doctors Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
          {doctors.map((doctor, index) => (
            <div key={index} className="group">
              <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-7 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
                  <div className="relative">
                    <img
                      src={doctor.image}
                      alt={doctor.name}
                      className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-white shadow-lg group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute -bottom-2 -right-2 bg-blue-600 text-white p-2 rounded-full shadow-md">
                      <Award className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{doctor.name}</h3>
                      <span className="hidden sm:inline-block px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200">Verified</span>
                    </div>
                    <p className="text-blue-600 font-semibold mb-3">{doctor.title}</p>
                    <QuoteDisplay quote={doctor.quote} />
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                        {doctor.specialties.map((specialty, idx) => (
                          <span key={idx} className="bg-blue-50 text-blue-700 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm border border-blue-200">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><strong>Experience:</strong> {doctor.experience}</p>
                      <p><strong>Education:</strong> {doctor.education}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Achievements */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 mb-12">
          {achievements.map((achievement, index) => (
            <div key={index} className="text-center group">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ring-1 ring-blue-200 group-hover:bg-blue-100 transition-colors duration-300">
                <achievement.icon className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{achievement.title}</h3>
              <p className="text-gray-600 text-sm">{achievement.subtitle}</p>
            </div>
          ))}
        </div>

        {/* Mission & Values */}
        <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 md:p-10 shadow-sm mb-10">
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Mission & Values</h3>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto px-4">
              To provide exceptional physiotherapy care that empowers individuals to overcome physical 
              challenges, prevent injuries, and achieve optimal health and wellness.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div key={index} className="flex items-start gap-3 rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow bg-gray-50/50">
                <div className="bg-blue-600 p-2 rounded-full mt-0.5">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5">{value.title}</h4>
                  <p className="text-sm sm:text-base text-gray-600">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        
        {/* Facilities & Equipment - Compact Showcase */}
        <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm mb-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
            <div className="text-center sm:text-left">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Facilities & Equipment</h3>
              <p className="text-sm sm:text-base text-gray-600">Quick glance at our setup and advanced rehab tools.</p>
            </div>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => scrollByCards('left')}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scrollByCards('right')}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
          </div>
        </div>
        
          <div className="relative">
            {/* Horizontal scroller */}
            <div
              ref={scrollerRef}
              className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2"
            >
              {facilityGallery.map((item, idx) => (
                <div
                  key={idx}
                  className="min-w-[220px] sm:min-w-[260px] snap-start rounded-xl border border-gray-100 overflow-hidden bg-gray-50/50 hover:shadow-md transition-shadow"
                >
                  <div className="w-full h-36 sm:h-40 overflow-hidden">
                    <img src={item.image} alt={item.title} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-2">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{item.title}</h4>
                  </div>
                </div>
              ))}
          </div>
        </div>

          <div className="mt-4 flex items-center justify-center">
            <button
              onClick={() => setShowVideoModal(true)}
              className="inline-flex items-center gap-2 text-blue-600 border border-blue-200 hover:bg-blue-50 px-4 py-2 rounded-full text-sm font-semibold"
            >
              <PlayCircle className="w-4 h-4" /> Watch Video Tour
          </button>
          </div>
        </div>

        {/* Video Modal */}
        {showVideoModal && (
          <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div
              className="relative bg-black rounded-2xl overflow-hidden shadow-2xl p-2 sm:p-3"
              style={{ width: 'min(92vw, 420px)' }}
            >
              <button
                onClick={() => setShowVideoModal(false)}
                className="absolute top-2 right-2 md:top-3 md:right-3 bg-white/95 hover:bg-white rounded-full p-2 md:p-2.5 shadow focus:outline-none"
                aria-label="Close video"
              >
                <X className="w-5 h-5 md:w-6 md:h-6 text-gray-800" />
              </button>
              <div className="w-full bg-white rounded-xl overflow-hidden">
                <blockquote
                  className="instagram-media"
                  data-instgrm-permalink={`${facilityVideoUrl}/?utm_source=ig_embed&utm_campaign=loading`}
                  data-instgrm-version="14"
                  style={{ background: '#fff', border: 0, margin: 0, padding: 0, width: '100%' }}
                >
                  <a href={facilityVideoUrl} target="_blank" rel="noopener noreferrer">
                    View on Instagram
                  </a>
                </blockquote>
              </div>
              <div className="mt-3 text-center">
                <a
                  href={facilityVideoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm"
                >
                  Open directly on Instagram
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const QuoteDisplay = ({ quote }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 150; // Adjust as needed

  if (quote.length <= maxLength) {
    return <p className="text-sm sm:text-base text-gray-600 mb-4 italic">"{quote}"</p>;
  }

  const displayedQuote = isExpanded ? quote : `${quote.substring(0, maxLength)}...`;

  return (
    <p className="text-sm sm:text-base text-gray-600 mb-4 italic">
      "{displayedQuote}"
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-blue-600 hover:underline ml-1 focus:outline-none"
      >
        {isExpanded ? 'Read Less' : 'Read More'}
      </button>
    </p>
  );
};

export default About;